// @flow

import React, { Component, Fragment } from "react";
import { bindAll, noop, isEqual, get, flatten, toArray } from "lodash";
import type { BinaryFn, NullaryFn } from "@tvg/types/Functional";
import type { RaceTypeCodeEnum } from "@tvg/types/Race";
import formatCurrency from "@tvg/formatter/currency";
import ProcessingOverlay from "@fdr/static-ui/ProcessingOverlay";
import type { BetType } from "@tvg/types/Bet";
import calculateTotals from "@tvg/api/wtx/BetHelper";
import pluralize from "@fdr/utils/generalUtils";
import mediator from "@tvg/mediator";
import { isXSell } from "@tvg/sh-utils/mobileUtils";

import Button from "../../_atoms/Button";
import {
  Wrapper,
  ConfirmationButtonContainer,
  BetSummaryContainer,
  FloatingBannerWrapper,
  WagerAmountContainer
} from "./styled-components";
import BetSummary from "../../_molecules/BetSummary";
import BetRaceLink from "../../_molecules/BetRaceLink";
import { FloatingBanner } from "../../_molecules/MessageV2";

export type BetStateType = "none" | "success" | "error";

export type BetGroupStatusType = "NONE" | "PLACED" | "ERROR" | "PROCESSING";

export type WagerTypesSelected = {
  horseNamesList: Array<string>,
  wagerType: BetType,
  selections: string[][],
  betTotal: number,
  wagerAmount: number
};

export type BetGroupStatus = {
  [key: string]: {
    status: BetGroupStatusType
  }
};

type Props = {
  /**
   * Race post time
   */
  postTime: string,
  /**
   * Race MTP value
   */
  mtp: number,
  /**
   * Race number
   */
  raceNumber: string,
  /**
   * Race type code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * Track name
   */
  trackName: string,
  /**
   * Enables greyhound flag
   */
  isGreyhound: boolean,
  /**
   * Total value of the bet
   */
  betTotal: number,
  /**
   * Wager amount
   */
  wagerAmount: number,
  /**
   * Wager types
   */
  wagerTypesSelected: WagerTypesSelected[],
  /**
   * Array of the bet selections
   */
  selections: string[][],
  /**
   * Callback to be called to confirm the bet
   */
  onConfirmation: NullaryFn<mixed>,
  /**
   * Callback to be called to retry the bet
   */
  onRetry: NullaryFn<mixed>,
  /**
   * Bet state for feedback overlay "none" (default) | "success" | "warning"
   */
  feedbackState?: BetStateType,
  /**
   * Message to display on feedback overlay
   */
  feedbackMessage: string,
  /**
   * Is Key flag
   */
  isKey: boolean,
  /**
   * Is processing request
   */
  isProcessing: boolean,
  /**
   * Is the bet placed successfully
   */
  isBetPlaced: boolean,
  /**
   * Is the bet NOT placed and error occur
   */
  isBettingError: boolean,
  /**
   * Message for betting placement error
   */
  errorBetMessage?:
    | string
    | { title: string, text: string, buttonActionType?: string },
  /**
   * Callback for retain button
   */
  onRetain?: NullaryFn<mixed>,
  /**
   * Callback for back to race button
   */
  onBackToRace?: NullaryFn<mixed>,
  /**
   * Is Leg flag
   */
  isLeg: boolean,
  /**
   * If Race Alerts bar should be shown
   */
  hasRaceAlertsBar: boolean,
  /**
   * Confirm text
   */
  confirmText: string,
  /**
   * Matrix of favorite runners
   */
  favoriteRunners: Array<Array<string>>,
  /**
   * repetition number
   */
  repetitionsNr: number,
  /**
   * On repeat callback
   */
  onRepeatClick: NullaryFn<void>,
  /**
   * Boolean that represents a toggle
   * to wether enable the repeat functionality
   */
  hasRepeaterEnabled: boolean,
  /**
   * The race alerts component
   */
  renderRaceAlerts: NullaryFn<mixed>,
  /*
   * add margin in header
   */
  hasMarginBorder: boolean,
  /*
   * Add border in Header
   */
  hasBottomBorder: boolean,
  /*
   * Bet group status
   */
  betGroupStatus?: BetGroupStatus,
  /*
   * Show Bet tag
   */
  showTag: boolean,
  hasVideo?: boolean,
  watchVideoActive?: boolean,
  onOpenVideo?: BinaryFn<string, string, void>,
  depositURL?: string,
  isExternalLink?: boolean,
  pgmDepositRedirectToggle?: boolean,
  forcedStop: boolean,
  errorBets: number,
  retryBetsToggle: boolean,
  scratchedSelectionMessage?: string,
  isSelectionScratched: boolean,
  onWarningClose?: NullaryFn<mixed>,
  onInsufficientFunds?: NullaryFn<void>,
  onResponsibleGamingError?: NullaryFn<void>,
  onExpiredToken?: NullaryFn<void>,
  isBetDisabled?: boolean
};

type State = {
  isLoading: boolean
};

export default class BetConfirmation extends Component<Props, State> {
  static defaultProps = {
    postTime: "",
    mtp: 0,
    raceNumber: "1",
    raceTypeCode: "T",
    trackName: "",
    isGreyhound: false,
    betTotal: 0,
    isCancelable: false,
    wagerAmount: 0,
    wagerTypesSelected: [],
    selections: [],
    onConfirmation: noop,
    onRetry: noop,
    feedbackState: "none",
    feedbackMessage: "placing bet...",
    isKey: false,
    isLeg: false,
    isBetPlaced: false,
    isBettingError: false,
    isProcessing: false,
    hasRaceAlertsBar: false,
    errorBetMessage: "",
    onRetain: noop,
    onBackToRace: noop,
    confirmText: "Confirm $amount Bet",
    favoriteRunners: [],
    repetitionsNr: 1,
    onRepeatClick: noop,
    hasRepeaterEnabled: false,
    renderRaceAlerts: noop,
    hasMarginBorder: false,
    hasBottomBorder: false,
    betGroupStatus: undefined,
    showTag: false,
    hasVideo: false,
    watchVideoActive: false,
    onOpenVideo: noop,
    pgmDepositRedirectToggle: false,
    isExternalLink: false,
    depositURL: "",
    forcedStop: false,
    errorBets: 0,
    retryBetsToggle: false,
    buttonActionType: "close",
    scratchedSelectionMessage: "",
    isSelectionScratched: false,
    onWarningClose: noop,
    onInsufficientFunds: noop,
    onResponsibleGamingError: noop,
    isBetDisabled: false
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false
    };

    bindAll(this, [
      "handleConfirmation",
      "handleRetry",
      "handleErrorBets",
      "renderButtons"
    ]);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      this.state.isLoading !== nextState.isLoading ||
      this.props.selections.length !== nextProps.selections.length ||
      !isEqual(this.props.betGroupStatus, nextProps.betGroupStatus) ||
      this.props.repetitionsNr !== nextProps.repetitionsNr ||
      this.props.hasRaceAlertsBar !== nextProps.hasRaceAlertsBar ||
      !isEqual(this.props, nextProps)
    );
  }

  handleConfirmation() {
    this.setState({ isLoading: true });

    this.props.onConfirmation();
  }

  handleRetry() {
    this.setState({ isLoading: true });

    this.props.onRetry();
  }

  handleErrorBets() {
    let betAmount = 0;
    let betCount = 0;
    const bets = toArray(this.props.betGroupStatus)
      .filter((bet) => bet.status === "ERROR")
      .map((bet) => {
        betAmount = bet.retryBet.betAmount;
        const totals = calculateTotals(
          bet.retryBet.betAmount,
          get(bet, "retryBet.wagerType", {
            type: { name: "", id: "" }
          }),
          bet.retryBet.betSelections
        );
        betCount += totals.betCount;
        return bet;
      });

    return {
      errorBets: bets.length,
      totalAmount: betAmount * betCount
    };
  }

  renderButtons() {
    if (this.props.isBetPlaced && !this.props.isBettingError) {
      return (
        <Fragment>
          <Button
            onClick={this.props.onRetain}
            qaLabel="betConfirmation-doneBtn"
            type="secondary"
            size="big"
          >
            Reuse selection
          </Button>
          <Button
            onClick={this.props.onBackToRace}
            type="primary"
            size="big"
            qaLabel="betConfirmation-retainRunnersBtn"
          >
            Done
          </Button>
        </Fragment>
      );
    }
    if (this.props.isBettingError) {
      const { errorBets, totalAmount } = this.handleErrorBets();
      const buttonActionType = get(
        this.props,
        "errorBetMessage.buttonType",
        "retry"
      );
      const isInsufficientFundsError =
        this.props.pgmDepositRedirectToggle && buttonActionType === "deposit";
      const isRetryButtonError =
        this.props.retryBetsToggle &&
        buttonActionType === "retry" &&
        totalAmount > 0;

      const isResponsibleGamingError = buttonActionType === "rgLimits";
      const isLoginButton = buttonActionType === "login";

      if (isLoginButton && this.props.onExpiredToken) {
        return (
          <Button
            type="primary"
            size="big"
            isStretched
            onClick={this.props.onExpiredToken}
            qaLabel="betConfirmation-loginBtn"
          >
            Log back in
          </Button>
        );
      }

      if (isInsufficientFundsError) {
        return (
          <Button
            type="primary"
            size="big"
            isStretched
            onClick={() => {
              // $FlowFixMe
              this.props.onInsufficientFunds?.();

              mediator.base.dispatch({
                type: "PPM_DEPOSIT_NOW_CLICK",
                destinationURL: this.props.depositURL
              });
            }}
            qaLabel="betConfirmation-closeBtn"
            url={this.props.depositURL}
            isExternalLink={this.props.isExternalLink}
          >
            Add Funds
          </Button>
        );
      }

      if (isRetryButtonError) {
        return (
          <Button
            type="betting"
            size="big"
            isStretched
            onClick={this.handleRetry}
            qaLabel="betConfirmation-retryBtn"
            isDisabled={this.props.isBetDisabled}
          >
            {`Retry ${pluralize(errorBets, "bet")} ${formatCurrency(
              totalAmount
            )}`}
          </Button>
        );
      }

      if (
        !isXSell() &&
        isResponsibleGamingError &&
        this.props.onResponsibleGamingError
      ) {
        return (
          <Button
            type="primary"
            size="big"
            isStretched
            onClick={this.props.onResponsibleGamingError}
            qaLabel="betConfirmation-rgLimitsBtn"
          >
            View Limits
          </Button>
        );
      }

      return (
        <Button
          type="primary"
          size="big"
          isStretched
          onClick={this.props.onRetain}
          qaLabel="betConfirmation-closeBtn"
        >
          Close
        </Button>
      );
    }
    if (this.props.isProcessing) {
      return (
        <Button
          type="primary"
          size="big"
          isDisabled
          qaLabel="betConfirmation-processing"
        >
          Processing...
        </Button>
      );
    }
    return (
      <Button
        type="betting"
        size="big"
        isStretched
        onClick={this.handleConfirmation}
        qaLabel="betConfirmation-confirmationButton"
        isDisabled={this.props.isBetDisabled}
      >
        <WagerAmountContainer>
          {`${this.props.confirmText.split("$amount")[0]} ${formatCurrency(
            get(this.props, "betTotal", 0)
          )} ${this.props.confirmText.split("$amount")[1]}`}
        </WagerAmountContainer>
      </Button>
    );
  }

  render() {
    const isResponsibleGamingError =
      get(this.props, "errorBetMessage.buttonType") === "rgLimits";

    return (
      <Fragment>
        {Object.keys(get(this.props, "betGroupStatus", {})).length <= 1 &&
          this.props.isProcessing && (
            <ProcessingOverlay smallText isRounded text="Placing bet" />
          )}
        <Wrapper data-qa-label="betConfirmation">
          {this.props.isBettingError && (
            <FloatingBannerWrapper>
              <FloatingBanner
                type="error"
                text={get(
                  this.props,
                  "errorBetMessage.text",
                  this.props.errorBetMessage
                )}
                title={get(this.props, "errorBetMessage.title")}
              />
            </FloatingBannerWrapper>
          )}
          {this.props.isSelectionScratched && (
            <FloatingBannerWrapper>
              <FloatingBanner
                type="warning"
                text={this.props.scratchedSelectionMessage}
                onClose={this.props.onWarningClose}
              />
            </FloatingBannerWrapper>
          )}
          {!isResponsibleGamingError && (
            <React.Fragment>
              <BetRaceLink
                mtp={this.props.mtp}
                postTime={this.props.postTime}
                trackName={this.props.trackName}
                raceNumber={this.props.raceNumber}
                isLink={false}
                hasHover={false}
                useNewProgramPageLayout
                hasMarginBorder={false}
                hasVideo={this.props.isBetPlaced ? this.props.hasVideo : false}
                watchVideoActive={
                  this.props.isBetPlaced ? this.props.watchVideoActive : false
                }
                onOpenVideo={this.props.onOpenVideo}
              />
              {this.props.wagerTypesSelected.map(
                ({
                  wagerType,
                  selections,
                  betTotal,
                  wagerAmount,
                  horseNamesList
                }) => {
                  const wagerTypeCode = get(wagerType, "type.code", "");
                  const betStatus = get(
                    this.props,
                    `betGroupStatus.${wagerTypeCode}.status`,
                    this.props.forcedStop && "ERROR"
                  );

                  return (
                    flatten(selections).length > 0 && (
                      <BetSummaryContainer
                        hasBottomMargin={
                          !this.props.hasRaceAlertsBar ||
                          this.props.wagerTypesSelected.length > 1
                        }
                        key={`betSummary-${wagerTypeCode}`}
                      >
                        <BetSummary
                          isCancelable={false}
                          betTotal={betTotal}
                          wagerType={get(wagerType, "type", "")}
                          horseNamesList={horseNamesList}
                          favoriteRunners={this.props.favoriteRunners}
                          selectionType={get(wagerType, "type.name", "")}
                          wagerAmount={wagerAmount}
                          selections={selections}
                          raceTypeCode={this.props.raceTypeCode}
                          isKey={get(wagerType, "isKey", false)}
                          isLeg={get(wagerType, "legCount", 1) > 1}
                          raceNumber={this.props.raceNumber}
                          isVertical
                          isBetPlaced={betStatus === "PLACED"}
                          isBettingError={betStatus === "ERROR"}
                          isProcessing={betStatus === "PROCESSING"}
                          showTag={this.props.forcedStop || this.props.showTag}
                        />
                      </BetSummaryContainer>
                    )
                  );
                }
              )}
            </React.Fragment>
          )}
          {this.props.hasRaceAlertsBar && this.props.renderRaceAlerts()}
          <ConfirmationButtonContainer
            withTopBorder={!isResponsibleGamingError}
          >
            {this.renderButtons()}
          </ConfirmationButtonContainer>
        </Wrapper>
      </Fragment>
    );
  }
}
